import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {ModalAlert} from '@/components/alerts';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import DictionarySettingsModal from './DictionarySettingsModal';
import usePaginatedData from '@/utils/usePaginatedData';
import {buildURL} from '@/utils/urlBuilder';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';
interface Dictionary {
    id: number;
    context: string;
    lang: string;
    active: boolean;
    createdOn: string;
    updatedOn?: string;
}

interface UserData {
    permissions?: string[];
}

const setSearchQueryState = async (
    setSearchQuery: (query: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const DictionaryListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedDictionaryId, setSelectedDictionaryId] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [dictionaryToDelete, setDictionaryToDelete] = useState<{ id: number, context: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const {
        data: dictionaries,
        loading: loadingDictionaries,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.dictionariesResource, {
        storeResolver: 'dictionaries',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const userCanAccessDictionaryResource = hasPermission('fs.dictionary.list', userData?.permissions, true);
    const canCreateDictionary = hasPermission('fs.dictionary.create', userData?.permissions, true);
    const canChangeDictionary = hasPermission('fs.dictionary.update', userData?.permissions, true);
    const canDeleteDictionary = hasPermission('fs.dictionary.delete', userData?.permissions, true);


    const deleteAction = useStoreActions((actions: any) => actions.dictionaries.deleteOne);

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (dictionaryId: number): void => {
        setSelectedDictionaryId(dictionaryId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedDictionaryId(0);
    };

    const openDeleteConfirm = (id: number, context: string): void => {
        setDictionaryToDelete({id, context});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setDeleteConfirmOpen(false);
        setDictionaryToDelete(null);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!dictionaryToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildURL('dictionaryDetails', {dictionary_id: dictionaryToDelete.id});
            await axios.delete(url);
            deleteAction(dictionaryToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            console.error('Failed to delete dictionary:', error);
            setIsDeleting(false);
        }
    };

    const active = currentPage || 1;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (loadingUserData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }

    if (!userCanAccessDictionaryResource && !loadingUserData) {
        return (
            <>
                <ConditionalNav>
                </ConditionalNav>
                <HelpTextBlock helpText={i18n.t('ht_lexicons_are.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('th_name.text'),
            accessorKey: 'context',
        },
        {
            id: 'i18ntthlastmodified9text',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (row: Dictionary) => {
                if (!row.updatedOn || row.updatedOn === '-') {
                    return configLoaded ? formatDateTime(row.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(row.updatedOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_language.text'),
            accessorKey: 'lang',
        },
        {
            header: i18n.t('th_active.text'),
            accessorKey: 'active',
            cell: ({getValue}: { getValue: () => boolean }) => {
                if (getValue() === true) {
                    return (
                        <Icon name="mdi-check" className="text-success" />
                    );
                } else {
                    return (
                        <Icon name="mdi-close" className="text-danger" />
                    );
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: { row: { original: Dictionary } }) => (
                <div className="d-inline-block">
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="dictionary-list-outlineinfo-btn"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeDictionary ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                        >
                                            <Icon name="mdi-settings" className="mr-2" />{i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="dictionary-list-btn-1">
                                                    <Icon name="mdi-settings" className="mr-2" />{i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                                {
                                    canDeleteDictionary ? (
                                        <Dropdown.Item
                                            onClick={() => openDeleteConfirm(cell.row.original.id, cell.row.original.context)}
                                        >
                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="dictionary-list-btn">
                                                    <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }

                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <ConditionalNav>
            </ConditionalNav>
            <HelpTextBlock helpText={i18n.t('ht_lexicons_are.text')} />
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    {
                        canCreateDictionary ? (
                            <Button
                                variant="info"
                                size="sm"
                                className="mb-2"
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-dictionary-btn"
                            >
                                {i18n.t('jt_add_new_lexicon.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="mb-2"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="dictionary-list-plus-btn">
                                        {i18n.t('jt_add_new_lexicon.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            {
                loadingDictionaries ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                    <TableComponent
                                        data={dictionaries}
                                        columns={columns}
                                    />
                                    <PaginationComponent
                                        pages={pages}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                    />
                            </div>
                        </div>

                        {
                            settingsModalOpen ? (
                                <DictionarySettingsModal
                                    modalOpen={settingsModalOpen}
                                    closeModal={closeSettingsModal}
                                    activeDictionaryId={selectedDictionaryId}
                                />
                            ) : null
                        }

                        <ModalAlert
                            isOpen={deleteConfirmOpen}
                            onClose={closeDeleteConfirm}
                            variant="error"
                            title={i18n.t('jt_confirm_deletion.text') || 'Confirm Deletion'}
                            message={
                                dictionaryToDelete
                                    ? `${i18n.t('es_are_you_sure_delete_lexicon.text')} "${dictionaryToDelete.context}"?`
                                    : i18n.t('es_are_you_sure_delete_lexicon.text')
                            }
                            actions={[
                                {
                                    label: i18n.t('jt_delete.text') || 'Delete',
                                    onClick: confirmDelete,
                                    variant: 'danger',
                                    loading: isDeleting
                                }
                            ]}
                            dismissible={!isDeleting}
                        />
                    </>
                )
            }
        </>
    );
});

export default DictionaryListView; 